import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mail, Phone, Plus, Star, Trash2, UserRound } from "lucide-react";

import api from "../../../lib/api";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { CrmEmpresaSeguimiento } from "./CrmEmpresaSeguimiento";
import { CrmEmpresaAuditoria } from "./CrmEmpresaAuditoria";
import { CrmOrigenSelect } from "./CrmOrigenSelect";
import { useAuth } from "../../../context/AuthContext";

interface TipoEmpresa {
  id: number;
  codigo: string;
  nombre: string;
}

interface EmpresaDetalle {
  id: number;
  idExterno: string | null;
  razonSocial: string;
  nombreComercial: string | null;
  cif: string | null;
  direccion: string | null;
  poblacion: string | null;
  codigoPostal: string | null;
  pais: string;
  personaContacto: string | null;
  telefono: string | null;
  emailContacto: string | null;
  estadoComercial: "prospect" | "cliente" | "inactivo";
  origen: string | null;
  responsableUserId: number | null;
  totalPolizas: number;
  totalContactos: number;
  totalOportunidades: number;
  tiposEmpresa: TipoEmpresa[];
}

interface Responsable {
  id: number;
  email: string;
  role: string;
}

interface CatalogosResponse {
  tiposEmpresa: TipoEmpresa[];
  responsables: Responsable[];
  productos: Producto[];
  etapas: Etapa[];
}

type EstadoOportunidad = "abierta" | "ganada" | "perdida";

interface Producto {
  id: number;
  codigo: string;
  nombre: string;
}

interface Etapa {
  id: number;
  codigo: string;
  nombre: string;
  resultado: EstadoOportunidad;
  probabilidadBps: number;
}

interface OportunidadForm {
  productoId: string;
  etapaId: string;
  titulo: string;
  primaEstimada: string;
  comisionEstimada: string;
  probabilidad: string;
  fechaCierrePrevista: string;
  origen: string;
  motivoPerdida: string;
}

interface Contacto {
  id: number;
  nombre: string;
  cargo: string | null;
  email: string | null;
  telefono: string | null;
  esPrincipal: boolean;
  activo: boolean;
  notas: string | null;
}

interface ContactosResponse {
  data: Contacto[];
}

interface PolizaEmpresa {
  id: number;
  id_externo: string;
  tipo_negocio: string;
  numero_poliza: string;
  aseguradora: string;
  estado: "activa" | "cancelada" | "archivada";
  fecha_efecto: string | null;
  fecha_vencimiento: string | null;
  prima_total_cents: number | null;
  comision_cents: number | null;
}

interface PolizasResponse {
  data: PolizaEmpresa[];
}

interface OportunidadEmpresa {
  id: number;
  id_externo: string | null;
  producto_id: number;
  producto: string;
  etapa_id: number;
  etapa: string;
  titulo: string;
  estado: EstadoOportunidad;
  prima_estimada_cents: number | null;
  comision_estimada_cents: number | null;
  probabilidad_bps: number | null;
  fecha_cierre_prevista: string | null;
  motivo_perdida: string | null;
  origen: string | null;
}

interface OportunidadesResponse {
  data: OportunidadEmpresa[];
}

interface ContactoForm {
  nombre: string;
  cargo: string;
  email: string;
  telefono: string;
  notas: string;
  esPrincipal: boolean;
}

const EMPTY_CONTACTO: ContactoForm = {
  nombre: "",
  cargo: "",
  email: "",
  telefono: "",
  notas: "",
  esPrincipal: false,
};

const EMPTY_OPORTUNIDAD: OportunidadForm = {
  productoId: "",
  etapaId: "",
  titulo: "",
  primaEstimada: "",
  comisionEstimada: "",
  probabilidad: "",
  fechaCierrePrevista: "",
  origen: "",
  motivoPerdida: "",
};

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? "No se pudo completar la operación";
  }
  return "No se pudo completar la operación";
}

function estadoBadge(estado: EmpresaDetalle["estadoComercial"]) {
  if (estado === "cliente") return <Badge variant="success">Cliente</Badge>;
  if (estado === "inactivo") return <Badge variant="warning">Inactivo</Badge>;
  return <Badge variant="info">Prospect</Badge>;
}

function estadoPolizaBadge(estado: PolizaEmpresa["estado"]) {
  if (estado === "activa") return <Badge variant="success">Activa</Badge>;
  if (estado === "cancelada") return <Badge variant="warning">Cancelada</Badge>;
  return <Badge variant="neutral">Archivada</Badge>;
}

function estadoOportunidadBadge(estado: EstadoOportunidad) {
  if (estado === "ganada") return <Badge variant="success">Ganada</Badge>;
  if (estado === "perdida") return <Badge variant="danger">Perdida</Badge>;
  return <Badge variant="info">Abierta</Badge>;
}

function formatDate(value: string | null): string {
  if (!value) return "Sin fecha";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function formatMoney(value: number | null): string {
  if (value === null) return "Pendiente";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(value / 100);
}

export function CrmEmpresaFichaModal({
  empresaId,
  onClose,
  onUpdated,
  onDeleted,
}: {
  empresaId: number | null;
  onClose: () => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
}) {
  const { user } = useAuth();
  const canAssignResponsible =
    user?.role === "admin" || user?.role === "director_comercial";
  const [empresa, setEmpresa] = useState<EmpresaDetalle | null>(null);
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [polizas, setPolizas] = useState<PolizaEmpresa[]>([]);
  const [oportunidades, setOportunidades] = useState<OportunidadEmpresa[]>([]);
  const oportunidadesSectionRef = useRef<HTMLElement>(null);
  const polizasSectionRef = useRef<HTMLElement>(null);
  const contactosSectionRef = useRef<HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState<ContactoForm>(EMPTY_CONTACTO);
  const [saving, setSaving] = useState(false);
  const [updatingContactId, setUpdatingContactId] = useState<number | null>(null);
  const [tiposDisponibles, setTiposDisponibles] = useState<TipoEmpresa[]>([]);
  const [responsables, setResponsables] = useState<Responsable[]>([]);
  const [editingCommercial, setEditingCommercial] = useState(false);
  const [commercialForm, setCommercialForm] = useState({
    razonSocial: "",
    nombreComercial: "",
    cif: "",
    direccion: "",
    poblacion: "",
    codigoPostal: "",
    pais: "España",
    personaContacto: "",
    telefono: "",
    emailContacto: "",
    estadoComercial: "prospect" as EmpresaDetalle["estadoComercial"],
    origen: "",
    responsableUserId: "",
    tipoIds: [] as number[],
  });
  const [savingCommercial, setSavingCommercial] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingProspect, setDeletingProspect] = useState(false);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [opportunityFormOpen, setOpportunityFormOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] =
    useState<OportunidadEmpresa | null>(null);
  const [opportunityForm, setOpportunityForm] =
    useState<OportunidadForm>(EMPTY_OPORTUNIDAD);
  const [opportunityError, setOpportunityError] = useState("");
  const [savingOpportunity, setSavingOpportunity] = useState(false);
  const [confirmingOpportunityDeleteId, setConfirmingOpportunityDeleteId] =
    useState<number | null>(null);
  const [deletingOpportunityId, setDeletingOpportunityId] =
    useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (empresaId === null) return;
    setLoading(true);
    setError("");

    try {
      const [
        empresaResponse,
        contactosResponse,
        catalogosResponse,
        polizasResponse,
        oportunidadesResponse,
      ] = await Promise.all([
        api.get<EmpresaDetalle>(`/admin/crm/clientes/${empresaId}`),
        api.get<ContactosResponse>(`/admin/crm/contactos/empresa/${empresaId}`),
        api.get<CatalogosResponse>("/admin/crm/catalogos"),
        api.get<PolizasResponse>("/admin/crm/polizas", {
          params: { cliente_id: empresaId, page: 1, limit: 100 },
        }),
        api.get<OportunidadesResponse>("/admin/crm/oportunidades", {
          params: { cliente_id: empresaId, page: 1, limit: 100 },
        }),
      ]);
      setEmpresa(empresaResponse.data);
      setContactos(contactosResponse.data.data);
      setPolizas(polizasResponse.data.data);
      setOportunidades(oportunidadesResponse.data.data);
      setTiposDisponibles(catalogosResponse.data.tiposEmpresa);
      setResponsables(catalogosResponse.data.responsables);
      setProductos(catalogosResponse.data.productos);
      setEtapas(catalogosResponse.data.etapas);
      setCommercialForm({
        razonSocial: empresaResponse.data.razonSocial,
        nombreComercial: empresaResponse.data.nombreComercial ?? "",
        cif: empresaResponse.data.cif ?? "",
        direccion: empresaResponse.data.direccion ?? "",
        poblacion: empresaResponse.data.poblacion ?? "",
        codigoPostal: empresaResponse.data.codigoPostal ?? "",
        pais: empresaResponse.data.pais || "España",
        personaContacto: empresaResponse.data.personaContacto ?? "",
        telefono: empresaResponse.data.telefono ?? "",
        emailContacto: empresaResponse.data.emailContacto ?? "",
        estadoComercial: empresaResponse.data.estadoComercial,
        origen: empresaResponse.data.origen ?? "",
        responsableUserId:
          empresaResponse.data.responsableUserId === null
            ? ""
            : String(empresaResponse.data.responsableUserId),
        tipoIds: empresaResponse.data.tiposEmpresa.map((tipo) => tipo.id),
      });
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    if (empresaId !== null) {
      setEmpresa(null);
      setContactos([]);
      setPolizas([]);
      setOportunidades([]);
      setShowContactForm(false);
      setEditingCommercial(false);
      setConfirmingDelete(false);
      setOpportunityFormOpen(false);
      setEditingOpportunity(null);
      setOpportunityForm(EMPTY_OPORTUNIDAD);
      setOpportunityError("");
      setConfirmingOpportunityDeleteId(null);
      setContactForm(EMPTY_CONTACTO);
      void loadData();
    }
  }, [empresaId, loadData]);

  const close = () => {
    if (
      saving ||
      savingOpportunity ||
      savingCommercial ||
      deletingProspect ||
      deletingOpportunityId !== null ||
      updatingContactId !== null
    ) return;
    onClose();
  };

  const createContact = async () => {
    if (empresaId === null) return;
    setError("");

    if (!contactForm.nombre.trim()) {
      setError("El nombre del contacto es obligatorio.");
      return;
    }

    setSaving(true);
    try {
      await api.post(`/admin/crm/contactos/empresa/${empresaId}`, {
        nombre: contactForm.nombre,
        cargo: contactForm.cargo || null,
        email: contactForm.email || null,
        telefono: contactForm.telefono || null,
        notas: contactForm.notas || null,
        es_principal: contactForm.esPrincipal,
      });
      setContactForm(EMPTY_CONTACTO);
      setShowContactForm(false);
      await loadData();
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const updateContact = async (
    contacto: Contacto,
    updates: { es_principal?: boolean; activo?: boolean }
  ) => {
    setUpdatingContactId(contacto.id);
    setError("");
    try {
      await api.patch(`/admin/crm/contactos/${contacto.id}`, updates);
      await loadData();
    } catch (updateError) {
      setError(getErrorMessage(updateError));
    } finally {
      setUpdatingContactId(null);
    }
  };

  const saveCommercial = async () => {
    if (empresaId === null) return;
    if (empresa?.estadoComercial === "prospect" && !commercialForm.razonSocial.trim()) {
      setError("La razón social del prospect es obligatoria.");
      return;
    }
    setSavingCommercial(true);
    setError("");
    try {
      const payload: Record<string, unknown> = {
        nombre_comercial: commercialForm.nombreComercial || null,
        estado_comercial: commercialForm.estadoComercial,
        origen: commercialForm.origen || null,
        tipo_empresa_ids: commercialForm.tipoIds,
      };
      if (empresa?.estadoComercial === "prospect") {
        Object.assign(payload, {
          razon_social: commercialForm.razonSocial.trim(),
          cif: commercialForm.cif.trim() || null,
          direccion: commercialForm.direccion.trim() || null,
          poblacion: commercialForm.poblacion.trim() || null,
          codigo_postal: commercialForm.codigoPostal.trim() || null,
          pais: commercialForm.pais.trim() || "España",
          persona_contacto: commercialForm.personaContacto.trim() || null,
          telefono: commercialForm.telefono.trim() || null,
          email_contacto: commercialForm.emailContacto.trim() || null,
        });
      }
      if (canAssignResponsible) {
        payload.responsable_user_id = commercialForm.responsableUserId
          ? Number(commercialForm.responsableUserId)
          : null;
      }
      await api.patch(`/admin/crm/clientes/${empresaId}/comercial`, payload);
      setEditingCommercial(false);
      await loadData();
      onUpdated?.();
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSavingCommercial(false);
    }
  };

  const deleteProspect = async () => {
    if (empresaId === null || empresa?.estadoComercial !== "prospect") return;
    setDeletingProspect(true);
    setError("");
    try {
      await api.delete(`/admin/crm/clientes/${empresaId}/prospect`);
      setConfirmingDelete(false);
      onDeleted?.();
      if (!onDeleted) onClose();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError));
    } finally {
      setDeletingProspect(false);
    }
  };

  const openOpportunityForm = () => {
    const initialStage = etapas.find((etapa) => etapa.codigo === "NUEVO");
    setEditingOpportunity(null);
    setConfirmingOpportunityDeleteId(null);
    setOpportunityForm({
      ...EMPTY_OPORTUNIDAD,
      etapaId: initialStage ? String(initialStage.id) : "",
      probabilidad: initialStage
        ? String(initialStage.probabilidadBps / 100)
        : "",
      origen: empresa?.origen ?? "",
    });
    setOpportunityError("");
    setOpportunityFormOpen(true);
  };

  const openEditOpportunity = (opportunity: OportunidadEmpresa) => {
    const opportunityStage = etapas.find(
      (etapa) => etapa.id === opportunity.etapa_id
    );
    setEditingOpportunity(opportunity);
    setOpportunityForm({
      productoId: String(opportunity.producto_id),
      etapaId: String(opportunity.etapa_id),
      titulo: opportunity.titulo,
      primaEstimada:
        opportunity.prima_estimada_cents === null
          ? ""
          : String(opportunity.prima_estimada_cents / 100),
      comisionEstimada:
        opportunity.comision_estimada_cents === null
          ? ""
          : String(opportunity.comision_estimada_cents / 100),
      probabilidad:
        opportunityStage?.resultado !== "abierta"
          ? String((opportunityStage?.probabilidadBps ?? 0) / 100)
          : opportunity.probabilidad_bps === null
            ? ""
            : String(opportunity.probabilidad_bps / 100),
      fechaCierrePrevista: opportunity.fecha_cierre_prevista ?? "",
      origen: opportunity.origen ?? "",
      motivoPerdida: opportunity.motivo_perdida ?? "",
    });
    setOpportunityError("");
    setConfirmingOpportunityDeleteId(null);
    setOpportunityFormOpen(true);
    window.setTimeout(() => {
      document
        .getElementById("crm-empresa-oportunidad-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const setOpportunityField = (
    field: keyof OportunidadForm,
    value: string
  ) => {
    setOpportunityForm((current) => ({ ...current, [field]: value }));
  };

  const saveOpportunity = async () => {
    if (empresaId === null) return;

    setOpportunityError("");
    if (!opportunityForm.productoId || !opportunityForm.etapaId) {
      setOpportunityError("Selecciona el producto y la fase comercial.");
      return;
    }

    const selectedStage = etapas.find(
      (etapa) => String(etapa.id) === opportunityForm.etapaId
    );
    if (
      selectedStage?.resultado === "perdida" &&
      !opportunityForm.motivoPerdida.trim()
    ) {
      setOpportunityError("Indica el motivo por el que se pierde la oportunidad.");
      return;
    }

    setSavingOpportunity(true);
    try {
      const payload = {
        crm_cliente_id: empresaId,
        producto_id: Number(opportunityForm.productoId),
        etapa_id: Number(opportunityForm.etapaId),
        titulo: opportunityForm.titulo.trim() || undefined,
        prima_estimada: opportunityForm.primaEstimada || null,
        comision_estimada: opportunityForm.comisionEstimada || null,
        probabilidad: opportunityForm.probabilidad || null,
        fecha_cierre_prevista:
          opportunityForm.fechaCierrePrevista || null,
        origen: opportunityForm.origen.trim() || null,
        motivo_perdida:
          selectedStage?.resultado === "perdida"
            ? opportunityForm.motivoPerdida.trim()
            : null,
      };

      if (editingOpportunity) {
        await api.patch(
          `/admin/crm/oportunidades/${editingOpportunity.id}`,
          payload
        );
      } else {
        await api.post("/admin/crm/oportunidades", payload);
      }

      setOpportunityFormOpen(false);
      setEditingOpportunity(null);
      setConfirmingOpportunityDeleteId(null);
      setOpportunityForm(EMPTY_OPORTUNIDAD);
      await loadData();
      onUpdated?.();
    } catch (saveError) {
      setOpportunityError(getErrorMessage(saveError));
    } finally {
      setSavingOpportunity(false);
    }
  };

  const deleteOpportunity = async (opportunity: OportunidadEmpresa) => {
    if (user?.role !== "admin") return;
    setDeletingOpportunityId(opportunity.id);
    setError("");
    try {
      await api.delete(`/admin/crm/oportunidades/${opportunity.id}`);
      setConfirmingOpportunityDeleteId(null);
      if (editingOpportunity?.id === opportunity.id) {
        setOpportunityFormOpen(false);
        setEditingOpportunity(null);
        setOpportunityForm(EMPTY_OPORTUNIDAD);
      }
      await loadData();
      onUpdated?.();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError));
    } finally {
      setDeletingOpportunityId(null);
    }
  };

  const address = empresa
    ? [
        empresa.direccion,
        [empresa.codigoPostal, empresa.poblacion].filter(Boolean).join(" "),
        empresa.pais,
      ]
        .filter(Boolean)
        .join(", ")
    : "";
  const selectedOpportunityStage = etapas.find(
    (etapa) => String(etapa.id) === opportunityForm.etapaId
  );

  return (
    <Modal
      isOpen={empresaId !== null}
      onClose={close}
      title={empresa?.nombreComercial || empresa?.razonSocial || "Ficha de empresa"}
      size="xl"
      footer={
        <Button
          variant="secondary"
          onClick={close}
          disabled={
            saving ||
            savingOpportunity ||
            savingCommercial ||
            deletingProspect ||
            deletingOpportunityId !== null
          }
        >
          Cerrar
        </Button>
      }
    >
      {loading && !empresa ? (
        <div className="py-12 text-center text-sm text-gray-500">
          Cargando ficha de empresa…
        </div>
      ) : (
        <div className="space-y-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {empresa && (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-[#1A3AB8]">
                      {empresa.idExterno ?? "Sin ID de cliente"}
                    </span>
                    {estadoBadge(empresa.estadoComercial)}
                    {empresa.tiposEmpresa.map((tipo) => (
                      <Badge key={tipo.id} variant="neutral">
                        {tipo.nombre}
                      </Badge>
                    ))}
                  </div>
                  {empresa.nombreComercial && (
                    <p className="mt-2 text-sm text-gray-600">
                      Razón social: {empresa.razonSocial}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-600">
                    CIF/NIF: {empresa.cif || "Sin indicar"}
                  </p>
                  {address && (
                    <p className="mt-1 text-sm text-gray-600">{address}</p>
                  )}
                  {empresa.origen && (
                    <p className="mt-1 text-sm text-gray-600">
                      Origen: {empresa.origen}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={openOpportunityForm}>
                    <Plus className="h-4 w-4" />
                    Nueva oportunidad
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditingCommercial((current) => !current)}
                  >
                    {empresa.estadoComercial === "prospect"
                      ? "Editar prospect"
                      : "Editar datos comerciales"}
                  </Button>
                  {user?.role === "admin" &&
                    empresa.estadoComercial === "prospect" && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setConfirmingDelete(true)}
                        disabled={deletingProspect}
                      >
                        <Trash2 className="h-4 w-4" />
                        Eliminar prospect
                      </Button>
                    )}
                </div>
              </div>

              {confirmingDelete && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-4">
                  <p className="font-semibold text-red-900">
                    ¿Eliminar definitivamente este prospect?
                  </p>
                  <p className="mt-1 text-sm text-red-800">
                    Se eliminarán también sus oportunidades, contactos, actividades y tareas.
                    Esta acción no se puede deshacer. Si tuviera alguna póliza vinculada,
                    la plataforma bloqueará la eliminación.
                  </p>
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={deletingProspect}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={deleteProspect}
                      loading={deletingProspect}
                    >
                      Eliminar definitivamente
                    </Button>
                  </div>
                </div>
              )}

              {editingCommercial && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    {empresa.estadoComercial === "prospect" && (
                      <>
                        <Input
                          label="Razón social *"
                          value={commercialForm.razonSocial}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              razonSocial: event.target.value,
                            }))
                          }
                        />
                        <Input
                          label="CIF o NIF"
                          value={commercialForm.cif}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              cif: event.target.value,
                            }))
                          }
                        />
                      </>
                    )}
                    <Input
                      label="Nombre comercial"
                      value={commercialForm.nombreComercial}
                      onChange={(event) =>
                        setCommercialForm((current) => ({
                          ...current,
                          nombreComercial: event.target.value,
                        }))
                      }
                    />
                    {canAssignResponsible && <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Situación comercial
                      </span>
                      <select
                        value={commercialForm.estadoComercial}
                        onChange={(event) =>
                          setCommercialForm((current) => ({
                            ...current,
                            estadoComercial: event.target
                              .value as EmpresaDetalle["estadoComercial"],
                          }))
                        }
                        className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="prospect" disabled={Boolean(empresa.idExterno)}>
                          Prospect
                        </option>
                        <option value="cliente">Cliente</option>
                        <option value="inactivo">Inactivo</option>
                      </select>
                    </label>}
                    <CrmOrigenSelect
                      value={commercialForm.origen}
                      onChange={(value) =>
                        setCommercialForm((current) => ({
                          ...current,
                          origen: value,
                        }))
                      }
                    />
                    {empresa.estadoComercial === "prospect" && (
                      <>
                        <Input
                          label="Dirección"
                          value={commercialForm.direccion}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              direccion: event.target.value,
                            }))
                          }
                        />
                        <Input
                          label="Población"
                          value={commercialForm.poblacion}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              poblacion: event.target.value,
                            }))
                          }
                        />
                        <Input
                          label="Código postal"
                          value={commercialForm.codigoPostal}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              codigoPostal: event.target.value,
                            }))
                          }
                        />
                        <Input
                          label="País"
                          value={commercialForm.pais}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              pais: event.target.value,
                            }))
                          }
                        />
                        <Input
                          label="Persona de contacto"
                          value={commercialForm.personaContacto}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              personaContacto: event.target.value,
                            }))
                          }
                        />
                        <Input
                          label="Teléfono"
                          type="tel"
                          value={commercialForm.telefono}
                          onChange={(event) =>
                            setCommercialForm((current) => ({
                              ...current,
                              telefono: event.target.value,
                            }))
                          }
                        />
                        <div className="md:col-span-2">
                          <Input
                            label="Email de contacto"
                            type="email"
                            value={commercialForm.emailContacto}
                            onChange={(event) =>
                              setCommercialForm((current) => ({
                                ...current,
                                emailContacto: event.target.value,
                              }))
                            }
                          />
                        </div>
                      </>
                    )}
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Responsable comercial
                      </span>
                      <select
                        value={commercialForm.responsableUserId}
                        onChange={(event) =>
                          setCommercialForm((current) => ({
                            ...current,
                            responsableUserId: event.target.value,
                          }))
                        }
                        className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="">Sin responsable</option>
                        {responsables.map((responsable) => (
                          <option key={responsable.id} value={responsable.id}>
                            {responsable.email}
                          </option>
                        ))}
                      </select>
                    </label>
                    <fieldset className="md:col-span-2">
                      <legend className="mb-2 text-sm font-medium text-gray-700">
                        Tipos de empresa
                      </legend>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {tiposDisponibles.map((tipo) => (
                          <label
                            key={tipo.id}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={commercialForm.tipoIds.includes(tipo.id)}
                              onChange={(event) =>
                                setCommercialForm((current) => ({
                                  ...current,
                                  tipoIds: event.target.checked
                                    ? [...current.tipoIds, tipo.id]
                                    : current.tipoIds.filter((id) => id !== tipo.id),
                                }))
                              }
                              className="h-4 w-4 rounded border-gray-300 text-[#1A3AB8]"
                            />
                            {tipo.nombre}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditingCommercial(false)}
                      disabled={savingCommercial}
                    >
                      Cancelar
                    </Button>
                    <Button size="sm" onClick={saveCommercial} loading={savingCommercial}>
                      Guardar datos comerciales
                    </Button>
                  </div>
                </div>
              )}

              {opportunityFormOpen && (
                <section
                  id="crm-empresa-oportunidad-form"
                  className="scroll-mt-4 rounded-lg border border-blue-200 bg-blue-50/50 p-4"
                >
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900">
                      {editingOpportunity
                        ? "Editar oportunidad"
                        : "Nueva oportunidad"}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {editingOpportunity
                        ? `Actualiza los datos de ${editingOpportunity.id_externo || editingOpportunity.titulo}.`
                        : `Se vinculará automáticamente a ${empresa.nombreComercial || empresa.razonSocial}.`}
                    </p>
                  </div>

                  {opportunityError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      {opportunityError}
                    </div>
                  )}

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Producto *
                      </span>
                      <select
                        value={opportunityForm.productoId}
                        onChange={(event) =>
                          setOpportunityField("productoId", event.target.value)
                        }
                        className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
                      >
                        <option value="">Selecciona un producto</option>
                        {productos.map((producto) => (
                          <option key={producto.id} value={producto.id}>
                            {producto.nombre}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">
                        Fase comercial *
                      </span>
                      <select
                        value={opportunityForm.etapaId}
                        onChange={(event) => {
                          const nextStage = etapas.find(
                            (etapa) => String(etapa.id) === event.target.value
                          );
                          setOpportunityForm((current) => ({
                            ...current,
                            etapaId: event.target.value,
                            probabilidad: nextStage
                              ? String(nextStage.probabilidadBps / 100)
                              : "",
                            motivoPerdida:
                              nextStage?.resultado === "perdida"
                                ? current.motivoPerdida
                                : "",
                          }));
                        }}
                        className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8]"
                      >
                        <option value="">Selecciona una fase</option>
                        {etapas.map((etapa) => (
                          <option key={etapa.id} value={etapa.id}>
                            {etapa.nombre}
                          </option>
                        ))}
                      </select>
                    </label>

                    <Input
                      label="Título"
                      placeholder="Se genera automáticamente si lo dejas vacío"
                      value={opportunityForm.titulo}
                      onChange={(event) =>
                        setOpportunityField("titulo", event.target.value)
                      }
                    />
                    <Input
                      label="Fecha de cierre prevista"
                      type="date"
                      value={opportunityForm.fechaCierrePrevista}
                      onChange={(event) =>
                        setOpportunityField(
                          "fechaCierrePrevista",
                          event.target.value
                        )
                      }
                    />
                    <Input
                      label="Prima estimada (€)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={opportunityForm.primaEstimada}
                      onChange={(event) =>
                        setOpportunityField("primaEstimada", event.target.value)
                      }
                    />
                    <Input
                      label="Comisión estimada (€)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={opportunityForm.comisionEstimada}
                      onChange={(event) =>
                        setOpportunityField("comisionEstimada", event.target.value)
                      }
                    />
                    <Input
                      label="Probabilidad (%)"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={opportunityForm.probabilidad}
                      disabled={selectedOpportunityStage?.resultado !== "abierta"}
                      onChange={(event) =>
                        setOpportunityField("probabilidad", event.target.value)
                      }
                    />
                    <CrmOrigenSelect
                      value={opportunityForm.origen}
                      onChange={(value) =>
                        setOpportunityField("origen", value)
                      }
                    />
                    {selectedOpportunityStage?.resultado === "perdida" && (
                      <div className="md:col-span-2">
                        <Input
                          label="Motivo de pérdida *"
                          value={opportunityForm.motivoPerdida}
                          onChange={(event) =>
                            setOpportunityField(
                              "motivoPerdida",
                              event.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={
                        savingOpportunity || deletingOpportunityId !== null
                      }
                      onClick={() => {
                        setOpportunityFormOpen(false);
                        setEditingOpportunity(null);
                        setOpportunityForm(EMPTY_OPORTUNIDAD);
                        setOpportunityError("");
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      loading={savingOpportunity}
                      disabled={deletingOpportunityId !== null}
                      onClick={saveOpportunity}
                    >
                      {editingOpportunity
                        ? "Guardar cambios"
                        : "Crear oportunidad"}
                    </Button>
                  </div>
                </section>
              )}

              <div className="grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() =>
                    oportunidadesSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                  aria-label="Ver las oportunidades de esta empresa"
                  className="group cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3AB8] focus-visible:ring-offset-2"
                >
                  <div className="text-2xl font-bold text-gray-900">
                    {empresa.totalOportunidades}
                  </div>
                  <div className="text-sm text-gray-600 group-hover:text-[#1A3AB8]">
                    Oportunidades · Ver detalle
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    polizasSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                  aria-label="Ver las pólizas de esta empresa"
                  className="group cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3AB8] focus-visible:ring-offset-2"
                >
                  <div className="text-2xl font-bold text-gray-900">
                    {empresa.totalPolizas}
                  </div>
                  <div className="text-sm text-gray-600 group-hover:text-[#1A3AB8]">
                    Pólizas · Ver detalle
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    contactosSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                  aria-label="Ver los contactos activos de esta empresa"
                  className="group cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3AB8] focus-visible:ring-offset-2"
                >
                  <div className="text-2xl font-bold text-gray-900">
                    {empresa.totalContactos}
                  </div>
                  <div className="text-sm text-gray-600 group-hover:text-[#1A3AB8]">
                    Contactos activos · Ver detalle
                  </div>
                </button>
              </div>

              <section
                ref={oportunidadesSectionRef}
                className="scroll-mt-4 space-y-4 border-t border-gray-200 pt-5"
              >
                <div>
                  <h3 className="font-semibold text-gray-900">Oportunidades</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Operaciones comerciales vinculadas a esta empresa.
                  </p>
                </div>

                {oportunidades.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 py-8 text-center text-sm text-gray-500">
                    Esta empresa todavía no tiene oportunidades vinculadas.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {oportunidades.map((oportunidad) => (
                      <article
                        key={oportunidad.id}
                        className="rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              {oportunidad.id_externo && (
                                <span className="font-semibold text-[#1A3AB8]">
                                  {oportunidad.id_externo}
                                </span>
                              )}
                              {estadoOportunidadBadge(oportunidad.estado)}
                            </div>
                            <p className="mt-1 font-medium text-gray-900">
                              {oportunidad.titulo}
                            </p>
                            <p className="mt-1 text-sm text-gray-600">
                              {oportunidad.producto} · {oportunidad.etapa}
                            </p>
                            {oportunidad.motivo_perdida && (
                              <p className="mt-2 text-sm text-gray-500">
                                Motivo de pérdida: {oportunidad.motivo_perdida}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 space-y-3">
                            <div className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                              <div>
                                <span className="block text-xs text-gray-500">
                                  Prima / comisión
                                </span>
                                <span className="font-medium text-gray-900">
                                  {formatMoney(oportunidad.prima_estimada_cents)} / {formatMoney(oportunidad.comision_estimada_cents)}
                                </span>
                              </div>
                              <div>
                                <span className="block text-xs text-gray-500">
                                  Probabilidad / cierre
                                </span>
                                <span className="font-medium text-gray-900">
                                  {oportunidad.probabilidad_bps === null
                                    ? "Sin probabilidad"
                                    : `${oportunidad.probabilidad_bps / 100}%`} · {formatDate(oportunidad.fecha_cierre_prevista)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={deletingOpportunityId !== null}
                                onClick={() => openEditOpportunity(oportunidad)}
                              >
                                Editar
                              </Button>
                              {user?.role === "admin" && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={deletingOpportunityId !== null}
                                  onClick={() => {
                                    setOpportunityError("");
                                    setConfirmingOpportunityDeleteId(
                                      oportunidad.id
                                    );
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Eliminar
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        {confirmingOpportunityDeleteId === oportunidad.id && (
                          <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-4">
                            <p className="font-semibold text-red-900">
                              ¿Eliminar definitivamente esta oportunidad?
                            </p>
                            <p className="mt-1 text-sm text-red-800">
                              Se eliminará también su historial de fases. Esta
                              acción no se puede deshacer y quedará bloqueada si
                              la oportunidad ya está vinculada a una póliza.
                            </p>
                            <div className="mt-4 flex flex-wrap justify-end gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={deletingOpportunityId !== null}
                                onClick={() =>
                                  setConfirmingOpportunityDeleteId(null)
                                }
                              >
                                No eliminar
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                loading={
                                  deletingOpportunityId === oportunidad.id
                                }
                                onClick={() => deleteOpportunity(oportunidad)}
                              >
                                Eliminar definitivamente
                              </Button>
                            </div>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section
                ref={polizasSectionRef}
                className="scroll-mt-4 space-y-4 border-t border-gray-200 pt-5"
              >
                <div>
                  <h3 className="font-semibold text-gray-900">Pólizas</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Pólizas vinculadas y datos de su vigencia más reciente.
                  </p>
                </div>

                {polizas.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 py-8 text-center text-sm text-gray-500">
                    Esta empresa todavía no tiene pólizas vinculadas.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {polizas.map((poliza) => (
                      <article
                        key={poliza.id}
                        className="rounded-lg border border-gray-200 bg-white p-4"
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-[#1A3AB8]">
                                {poliza.id_externo}
                              </span>
                              {estadoPolizaBadge(poliza.estado)}
                            </div>
                            <p className="mt-1 font-medium text-gray-900">
                              {poliza.tipo_negocio}
                            </p>
                            <p className="mt-1 text-sm text-gray-600">
                              {poliza.aseguradora} · {poliza.numero_poliza}
                            </p>
                          </div>
                          <div className="grid shrink-0 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                            <div>
                              <span className="block text-xs text-gray-500">Vigencia</span>
                              <span className="font-medium text-gray-900">
                                {formatDate(poliza.fecha_efecto)}–{formatDate(poliza.fecha_vencimiento)}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs text-gray-500">Prima / comisión</span>
                              <span className="font-medium text-gray-900">
                                {formatMoney(poliza.prima_total_cents)} / {formatMoney(poliza.comision_cents)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section
                ref={contactosSectionRef}
                className="scroll-mt-4 space-y-4 border-t border-gray-200 pt-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Contactos</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Personas vinculadas a esta misma empresa.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setShowContactForm((current) => !current);
                      setError("");
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Añadir contacto
                  </Button>
                </div>

                {showContactForm && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input
                        label="Nombre *"
                        value={contactForm.nombre}
                        onChange={(event) =>
                          setContactForm((current) => ({
                            ...current,
                            nombre: event.target.value,
                          }))
                        }
                      />
                      <Input
                        label="Cargo"
                        value={contactForm.cargo}
                        onChange={(event) =>
                          setContactForm((current) => ({
                            ...current,
                            cargo: event.target.value,
                          }))
                        }
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={contactForm.email}
                        onChange={(event) =>
                          setContactForm((current) => ({
                            ...current,
                            email: event.target.value,
                          }))
                        }
                      />
                      <Input
                        label="Teléfono"
                        type="tel"
                        value={contactForm.telefono}
                        onChange={(event) =>
                          setContactForm((current) => ({
                            ...current,
                            telefono: event.target.value,
                          }))
                        }
                      />
                      <div className="md:col-span-2">
                        <Input
                          label="Notas"
                          value={contactForm.notas}
                          onChange={(event) =>
                            setContactForm((current) => ({
                              ...current,
                              notas: event.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={contactForm.esPrincipal}
                        onChange={(event) =>
                          setContactForm((current) => ({
                            ...current,
                            esPrincipal: event.target.checked,
                          }))
                        }
                        className="h-4 w-4 rounded border-gray-300 text-[#1A3AB8]"
                      />
                      Marcar como contacto principal
                    </label>
                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setShowContactForm(false);
                          setContactForm(EMPTY_CONTACTO);
                        }}
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                      <Button size="sm" onClick={createContact} loading={saving}>
                        Guardar contacto
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {contactos.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 py-8 text-center text-sm text-gray-500">
                      Esta empresa todavía no tiene contactos.
                    </div>
                  ) : (
                    contactos.map((contacto) => (
                      <div
                        key={contacto.id}
                        className={`rounded-lg border p-4 ${
                          contacto.activo
                            ? "border-gray-200 bg-white"
                            : "border-gray-200 bg-gray-50 opacity-70"
                        }`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <UserRound className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-gray-900">
                                {contacto.nombre}
                              </span>
                              {contacto.esPrincipal && (
                                <Badge variant="info">Principal</Badge>
                              )}
                              {!contacto.activo && (
                                <Badge variant="neutral">Inactivo</Badge>
                              )}
                            </div>
                            {contacto.cargo && (
                              <p className="mt-1 text-sm text-gray-600">
                                {contacto.cargo}
                              </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
                              {contacto.email && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Mail className="h-4 w-4" />
                                  {contacto.email}
                                </span>
                              )}
                              {contacto.telefono && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Phone className="h-4 w-4" />
                                  {contacto.telefono}
                                </span>
                              )}
                            </div>
                            {contacto.notas && (
                              <p className="mt-2 text-sm text-gray-500">
                                {contacto.notas}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 flex-wrap gap-2">
                            {contacto.activo && !contacto.esPrincipal && (
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={updatingContactId !== null}
                                onClick={() =>
                                  updateContact(contacto, { es_principal: true })
                                }
                              >
                                <Star className="h-4 w-4" />
                                Hacer principal
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={updatingContactId !== null}
                              onClick={() =>
                                updateContact(contacto, {
                                  activo: !contacto.activo,
                                })
                              }
                            >
                              {contacto.activo ? "Desactivar" : "Reactivar"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <CrmEmpresaSeguimiento
                empresaId={empresa.id}
                contactos={contactos}
              />

              <CrmEmpresaAuditoria empresaId={empresa.id} />
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
